import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { toast } from "sonner";
import { UploadCloud, FileCheck2, AlertTriangle, CircleAlert, Trash2, Loader2, Replace, BellRing } from "lucide-react";

const REMINDER_KEYS = ["sars", "bee", "coida"];

function daysUntil(expiry) {
  if (!expiry) return null;
  return Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
}

function expiryStatus(expiry) {
  if (!expiry) return null;
  const now = new Date();
  const d = new Date(expiry);
  const days = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: "Expired", cls: "bg-red-50 text-red-800 border-red-200", icon: CircleAlert };
  if (days <= 30) return { label: `Expires in ${days}d`, cls: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle };
  return { label: `Valid · ${days}d left`, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: FileCheck2 };
}

export default function Compliance() {
  const { meta } = useAuth();
  const [docs, setDocs] = useState({});
  const [uploading, setUploading] = useState("");
  const [expiryInputs, setExpiryInputs] = useState({});

  const load = async () => {
    const { data } = await api.get("/documents/me");
    const m = {};
    data.forEach((d) => { m[d.doc_type] = d; });
    setDocs(m);
  };
  useEffect(() => { load(); }, []);

  const upload = async (docType, file, hasExpiry) => {
    if (!file) return;
    setUploading(docType);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    if (hasExpiry && expiryInputs[docType]) fd.append("expiry_date", expiryInputs[docType]);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading("");
    }
  };

  const remove = async (docType) => {
    if (!window.confirm("Remove this document?")) return;
    await api.delete(`/documents/${docType}`);
    toast.success("Document removed");
    load();
  };

  const docTypes = meta?.doc_types || [];
  const uploadedCount = Object.keys(docs).length;
  const pct = docTypes.length ? Math.round((uploadedCount / docTypes.length) * 100) : 0;

  return (
    <div>
      <PageHeader eyebrow="SMME" title="Compliance Documents"
        subtitle="Upload and maintain your statutory documents. Replace any file at any time." />

      {(() => {
        const reminders = docTypes
          .filter((dt) => REMINDER_KEYS.includes(dt.key) && docs[dt.key])
          .map((dt) => ({ dt, days: daysUntil(docs[dt.key].expiry_date), expiry: docs[dt.key].expiry_date }))
          .filter((r) => r.days !== null && r.days <= 30)
          .sort((a, b) => a.days - b.days);
        const missingExpiry = docTypes.filter((dt) => REMINDER_KEYS.includes(dt.key) && docs[dt.key] && !docs[dt.key].expiry_date);
        if (reminders.length === 0 && missingExpiry.length === 0) return null;
        return (
          <div data-testid="expiry-reminder-banner" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <BellRing className="h-5 w-5 text-amber-600" />
              <p className="font-display font-bold text-amber-900">Expiry Reminders</p>
            </div>
            <ul className="space-y-2">
              {reminders.map((r) => (
                <li key={r.dt.key} data-testid={`expiry-reminder-${r.dt.key}`} className="flex items-center justify-between text-sm bg-white/70 rounded-md px-3 py-2 border border-amber-200">
                  <span className="font-medium text-slate-800">{r.dt.label}</span>
                  <span className={`font-semibold ${r.days < 0 ? "text-red-700" : "text-amber-700"}`}>
                    {r.days < 0 ? `Expired on ${r.expiry}` : r.days === 0 ? `Expires today (${r.expiry})` : `Expires in ${r.days} day${r.days === 1 ? "" : "s"} (${r.expiry})`}
                  </span>
                </li>
              ))}
              {missingExpiry.map((dt) => (
                <li key={dt.key} className="flex items-center justify-between text-sm bg-white/70 rounded-md px-3 py-2 border border-amber-200">
                  <span className="font-medium text-slate-800">{dt.label}</span>
                  <span className="font-semibold text-slate-500">No expiry date set — add one above</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-amber-800 mt-3">We'll also email you automatic reminders before SARS, BEE and COIDA certificates expire.</p>
          </div>
        );
      })()}

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="font-semibold text-slate-900">Compliance Completeness</p>
          <span className="font-mono-data text-sm font-semibold text-[#00427A]">{uploadedCount}/{docTypes.length} · {pct}%</span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#00427A] rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {docTypes.map((dt) => {
          const existing = docs[dt.key];
          const status = existing && dt.has_expiry ? expiryStatus(existing.expiry_date) : null;
          return (
            <div key={dt.key} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{dt.label}</h3>
                  {existing ? (
                    <p className="text-xs text-slate-500 mt-1 truncate max-w-[220px]">{existing.original_filename}</p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-1">Not uploaded</p>
                  )}
                </div>
                {status && (
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${status.cls}`}>
                    <status.icon className="h-3.5 w-3.5" /> {status.label}
                  </span>
                )}
                {existing && !dt.has_expiry && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-200">
                    <FileCheck2 className="h-3.5 w-3.5" /> Uploaded
                  </span>
                )}
              </div>

              {dt.has_expiry && (
                <div className="mt-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Expiry Date</label>
                  <input type="date" data-testid={`doc-${dt.key}-expiry`}
                    defaultValue={existing?.expiry_date || ""}
                    onChange={(e) => setExpiryInputs({ ...expiryInputs, [dt.key]: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
                </div>
              )}

              <div className="flex items-center gap-2 mt-4">
                <label data-testid={`doc-upload-${dt.key}-label`}
                  className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold cursor-pointer border border-[#00427A] text-[#00427A] hover:bg-[#00427A]/5 px-4 py-2 rounded-md transition-colors">
                  {uploading === dt.key ? <Loader2 className="h-4 w-4 animate-spin" /> : existing ? <Replace className="h-4 w-4" /> : <UploadCloud className="h-4 w-4" />}
                  {existing ? "Replace" : "Upload"}
                  <input data-testid={`doc-upload-${dt.key}-input`} type="file" className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    onChange={(e) => upload(dt.key, e.target.files[0], dt.has_expiry)} />
                </label>
                {existing && (
                  <button data-testid={`doc-delete-${dt.key}`} onClick={() => remove(dt.key)}
                    className="p-2 text-slate-400 hover:text-red-600 border border-slate-200 rounded-md"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
